import React, { useEffect, useMemo, useState } from "react";
import TimeSeriesChart from "./components/TimeSeriesChart";
import TopKTable from "./components/TopKTable";
import { promQuery, promQueryRange, rangeWindowMs, toSeries } from "./prom";
import { getTenantUuid } from "@/app/ingress/tenant";

// Stable palette (same as TimeSeriesChart default palette, but we apply deterministically by slug)
const PALETTE = [
  "#2563eb", // blue
  "#16a34a", // green
  "#f59e0b", // amber
  "#dc2626", // red
  "#7c3aed", // violet
  "#0891b2", // cyan
  "#db2777", // pink
  "#65a30d", // lime
  "#ea580c", // orange
  "#64748b", // slate
];

// Deterministic string -> color index (stable across refreshes)
function hashToIndex(s: string, mod: number): number {
  const str = String(s || "");
  let h = 2166136261; // FNV-1a-ish
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // make positive
  return Math.abs(h) % Math.max(1, mod);
}

export default function AdminObservabilityFlowVersionHealthPage() {
  const tenantUuid = getTenantUuid();

  const [p95Series, setP95Series] = useState<any[]>([]);
  const [topSlow, setTopSlow] = useState<Array<Record<string, any>>>([]);
  const [successBySlug, setSuccessBySlug] = useState<Array<Record<string, any>>>([]);

  useEffect(() => {
    let alive = true;

    async function run() {
      // p95 flow duration over time (keep range <= backend guardrail, typically 2h)
      try {
        const { start, end } = rangeWindowMs(2);
        const q = `histogram_quantile(0.95, sum by (le,slug) (rate(flow_run_duration_seconds_by_tenant_bucket{tenant_id="${tenantUuid}"}[10m])))`;
        const resp = await promQueryRange(q, tenantUuid, { start, end, step: "60s" });
        const ss = toSeries(resp);

        // Assign stable color per slug so each line is visually distinct
        const series = ss.map((s) => {
          const slug = (s.metric.slug || "unknown").trim() || "unknown";
          const color = PALETTE[hashToIndex(slug, PALETTE.length)];
          return { name: slug, points: s.points, color };
        });

        // Optional: stable sorting by slug for legend order consistency
        series.sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)));

        if (!alive) return;
        setP95Series(series);
      } catch {
        if (!alive) return;
        setP95Series([]);
      }

      // top slow slugs (current p95)
      try {
        const q = `topk(10, histogram_quantile(0.95, sum by (le,slug) (rate(flow_run_duration_seconds_by_tenant_bucket{tenant_id="${tenantUuid}"}[10m]))))`;
        //const q = `topk(10, histogram_quantile(0.95, sum by (le,slug) (rate(flow_run_duration_seconds_bucket[10m]))))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss.map((s) => ({
          slug: s.metric.slug || "unknown",
          p95_s: s.points[0]?.v ?? 0,
        }));
        if (!alive) return;
        setTopSlow(rows.sort((a, b) => (b.p95_s ?? 0) - (a.p95_s ?? 0)));
      } catch {
        if (!alive) return;
        setTopSlow([]);
      }

      // success rate by slug (last 15m): succeeded / total
      try {
        const q = `
(
  sum by (slug) (increase(ingress_requests_total_by_tenant_total{tenant_id="${tenantUuid}",status="succeeded"}[15m]))
)
/
clamp_min(sum by (slug) (increase(ingress_requests_total_by_tenant_total{tenant_id="${tenantUuid}"}[15m])), 0.000001)
        `.trim();
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss.map((s) => ({
          slug: s.metric.slug || "unknown",
          success_rate_15m: s.points[0]?.v ?? 0,
        }));
        if (!alive) return;
        setSuccessBySlug(rows.sort((a, b) => (a.success_rate_15m ?? 0) - (b.success_rate_15m ?? 0)));
      } catch {
        if (!alive) return;
        setSuccessBySlug([]);
      }
    }

    run();
    const t = window.setInterval(run, 20_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [tenantUuid]);

  // Useful for debugging in UI if needed later (not displayed now)
  useMemo(() => {
    return p95Series.map((s: any) => ({ name: s.name, color: s.color }));
  }, [p95Series]);

  return (
    <div className="p-6 space-y-6">
      <div className="text-xl font-semibold">Observability · Flow & Version Health</div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TimeSeriesChart title="Flow run duration p95 (by slug)" series={p95Series} height={340} />
        <TopKTable
          title="Slowest slugs (p95 seconds, last 10m)"
          columns={[
            { key: "slug", label: "slug", mono: true },
            { key: "p95_s", label: "p95_s" },
          ]}
          rows={topSlow.map((r) => ({ ...r, p95_s: (r.p95_s ?? 0).toFixed(3) }))}
        />
      </div>

      <TopKTable
        title="Success rate (last 15m) by slug"
        columns={[
          { key: "slug", label: "slug", mono: true },
          { key: "success_rate_15m", label: "success_rate_15m" },
        ]}
        rows={successBySlug.map((r) => ({
          ...r,
          success_rate_15m: ((r.success_rate_15m ?? 0) * 100).toFixed(2) + "%",
        }))}
      />
    </div>
  );
}